import React, { useState } from 'react';
import { getFileSrc } from '@/utils/helpers';
import { Button, StatusLine } from '@/components';
import { Flex, Body1Light, Body3Light, RobotoBody4 } from '@/ui';
import { COLORS } from '@/utils/static/colors';
import styled from 'styled-components';
import TempAvatar from '@/assets/icons/avatar.png';
import { Popconfirm, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Img } from '@/components/common/atoms';
import { CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import { UpdateNameModal } from '@/components/admin';

/**
 * @param avatarUrl - server img url
 * @param avatar - front img url
 * @param name
 * @param region
 * @param position
 * @param verified
 * @param size - ['large' (def), 'medium', 'small']
 * @param nameOnly - true/false
 * @param props
 */

export const ProfileViewer = ({
  avatarUrl,
  avatar,
  firstName,
  lastName,
  region,
  position,
  verified = false,
  size = 'large',
  nameOnly = false,
  adminView,
  refetchInvestorInfo,
  onDelete,
  structureView,
  ...props
}) => {
  const { t } = useTranslation();
  const [isOpenChangeNameModal, setIsOpenChangeNameModal] = useState(false);

  const RenderTitle = (firstName, lastName, size) => {
    switch (size) {
      case 'small':
        return (
          <Body3Light>
            {firstName} {lastName}
          </Body3Light>
        );
      case 'medium':
        return (
          <Body1Light>
            {firstName} {lastName}
          </Body1Light>
        );
      default:
        return (
          <Typography.Title level={5}>
            {firstName} {lastName}
          </Typography.Title>
        );
    }
  };

  return (
    <Flex align={'center'} {...props}>
      {!nameOnly && (
        <Avatar
          src={avatar ? avatar : avatarUrl ? getFileSrc(avatarUrl) : TempAvatar}
          $size={size}
        />
      )}
      <div>
        {RenderTitle(firstName, lastName, size)}

        {size === 'large' && (
          <Flex $margin={-4} align={'center'} $marginTop={4}>
            {region && <Body3Light $color={COLORS.greyMedium}>{region}</Body3Light>}
            {position && <RobotoBody4 $color={COLORS.aragonBlue}>{position}</RobotoBody4>}
            {verified && <StatusLine type={'accepted'} title={t('Verified')} colored />}
          </Flex>
        )}
      </div>

      {onDelete && structureView && (
        <Popconfirm
          placement='topRight'
          title={t('Are you sure you want to delete this referral connection?')}
          onConfirm={onDelete}
          okText={t('Yes')}
          cancelText={t('No')}
        >
          <DeleteIcon>
            <CloseCircleOutlined />
          </DeleteIcon>
        </Popconfirm>
      )}

      {adminView && (
        <>
          <div style={{ marginLeft: '10px' }}>
            <Button
              type='text'
              icon={<EditOutlined />}
              onClick={() => setIsOpenChangeNameModal(true)}
            />
          </div>
          <UpdateNameModal
            open={isOpenChangeNameModal}
            onClose={() => setIsOpenChangeNameModal(false)}
            initialFirstName={firstName}
            initialLastName={lastName}
            refetchInvestorInfo={refetchInvestorInfo}
          />
        </>
      )}
    </Flex>
  );
};

export const ProfileViewerSkeleton = () => (
  <Space>
    <Skeleton.Avatar shape={'circle'} active />
    <Skeleton.Input $size={'small'} active />
  </Space>
);

const AvatarSize = {
  large: '64px',
  medium: '32px',
  small: '24px'
};
const MarginBySize = {
  large: '16px',
  medium: '8px',
  small: '8px'
};

const DeleteIcon = styled.div`
  width: 18px;
  height: 18px;
  font-size: 18px;
  color: ${COLORS.iconColor};
  margin-left: 12px;
  margin-bottom: 7px;
  cursor: pointer;

  &:hover {
    color: ${COLORS.greyLight};
  }
`;

const Avatar = styled(Img)`
  width: ${(p) => AvatarSize[p.$size]};
  height: ${(p) => AvatarSize[p.$size]};
  border-radius: 100%;
  margin-right: ${(p) => MarginBySize[p.$size]};
  object-fit: cover;
`;
